import { Routes } from '@angular/router';
import { authGuard, adminGuard, superAdminGuard } from './core/auth/auth.guard';
import { spaceResolver } from './core/auth/slug.resolver';

export const routes: Routes = [

  // ── Landing pública ─────────────────────────────────────────────────────
  {
    path: '',
    loadComponent: () =>
      import('./features/landing/landing.component').then(m => m.LandingComponent)
  },

  // ── Registro público de espacio ─────────────────────────────────────────
  {
    path: 'crear-espacio',
    loadComponent: () =>
      import('./features/register-space/register-space.component').then(m => m.RegisterSpaceComponent)
  },
  {
    path: 'terminos',
    loadComponent: () =>
      import('./features/terms/terms.component').then(m => m.TermsComponent)
  },

  // ── Super admin (sin slug de club) ──────────────────────────────────────
  {
    path: 'super-admin/login',
    loadComponent: () =>
      import('./features/super-admin/super-admin-login.component').then(m => m.SuperAdminLoginComponent)
  },
  {
    path: 'super-admin',
    canActivate: [superAdminGuard],
    loadComponent: () =>
      import('./features/super-admin/super-admin.component').then(m => m.SuperAdminComponent)
  },

  // ── Rutas por club (:slug) ───────────────────────────────────────────────
  {
    path: ':slug',
    resolve: { space: spaceResolver },
    children: [

      { path: '', redirectTo: 'home', pathMatch: 'full' },

      // Auth
      {
        path: 'login',
        loadComponent: () =>
          import('./features/auth/login/login.component').then(m => m.LoginComponent)
      },
      {
        path: 'register',
        loadComponent: () =>
          import('./features/auth/register/register.component').then(m => m.RegisterComponent)
      },
      {
        path: 'join/:token',
        loadComponent: () =>
          import('./features/auth/join/join.component').then(m => m.JoinComponent)
      },
      {
        path: 'pending',
        loadComponent: () =>
          import('./features/auth/pending/pending.component').then(m => m.PendingComponent)
      },

      // Member
      {
        path: 'home',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/home/home.component').then(m => m.HomeComponent)
      },
      {
        path: 'reservations',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/reservations/reservations-page/reservations-page.component')
            .then(m => m.ReservationsPageComponent)
      },
      {
        path: 'reservations/book/:typeId',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/reservations/booking/booking.component')
            .then(m => m.BookingComponent)
      },
      {
        path: 'reservations/my',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/reservations/my-reservations/my-reservations.component')
            .then(m => m.MyReservationsComponent)
      },
      {
        path: 'community',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/community/announcements/announcements.component')
            .then(m => m.AnnouncementsComponent)
      },
      {
        path: 'wall',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/community/wall/wall.component').then(m => m.WallComponent)
      },
      {
        path: 'events',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/events/events.component').then(m => m.EventsComponent)
      },
      {
        path: 'chat',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/community/chat/chat.component').then(m => m.ChatComponent)
      },
      {
        path: 'chat/global',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/community/chat/chat-global.component')
            .then(m => m.ChatGlobalComponent)
      },
      {
        path: 'chat/:id',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/messages/message-thread.component')
            .then(m => m.MessageThreadComponent)
      },
      { path: 'messages',     redirectTo: 'chat',    pathMatch: 'full' },
      { path: 'messages/:id', redirectTo: 'chat/:id', pathMatch: 'full' },
      {
        path: 'polls',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/community/polls/polls.component').then(m => m.PollsComponent)
      },
      {
        path: 'rankings',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/rankings/rankings.component').then(m => m.RankingsComponent)
      },
      {
        path: 'my-qr',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/qr/member-qr.component').then(m => m.MemberQrComponent)
      },
      {
        path: 'admin/scan-qr',
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./features/qr/admin-scan-qr.component').then(m => m.AdminScanQrComponent)
      },
      {
        path: 'clubs',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/clubs/clubs.component').then(m => m.ClubsComponent)
      },
      {
        path: 'clubs/:clubId',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/clubs/club-detail.component').then(m => m.ClubDetailComponent)
      },
      {
        path: 'invite-friend',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/community/invite-friend/invite-friend.component')
            .then(m => m.InviteFriendComponent)
      },

      // Admin
      {
        path: 'admin',
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./features/admin/dashboard/dashboard.component')
            .then(m => m.AdminDashboardComponent)
      },
      {
        path: 'admin/dashboard',
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./features/admin/dashboard/dashboard.component')
            .then(m => m.AdminDashboardComponent)
      },
      {
        path: 'admin/facility-types',
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./features/admin/facility-types/facility-types.component')
            .then(m => m.FacilityTypesComponent)
      },
      {
        path: 'admin/facility-types/new',
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./features/admin/facility-type-form/facility-type-form.component')
            .then(m => m.FacilityTypeFormComponent)
      },
      {
        path: 'admin/facility-types/:id/edit',
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./features/admin/facility-type-form/facility-type-form.component')
            .then(m => m.FacilityTypeFormComponent)
      },
      {
        path: 'admin/facility-types/:typeId/facilities',
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./features/admin/facilities/facilities.component')
            .then(m => m.FacilitiesComponent)
      },
      {
        path: 'admin/facilities/:facilityId/rules',
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./features/admin/availability-rules/availability-rules.component')
            .then(m => m.AvailabilityRulesComponent)
      },
      {
        path: 'admin/members',
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./features/admin/members/members.component').then(m => m.MembersComponent)
      },
      {
        path: 'admin/dues',
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./features/admin/dues/dues.component').then(m => m.DuesComponent)
      },
      {
        path: 'admin/announcements',
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./features/admin/announcements/admin-announcements.component')
            .then(m => m.AdminAnnouncementsComponent)
      },
      {
        path: 'admin/events',
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./features/admin/events/admin-events.component')
            .then(m => m.AdminEventsComponent)
      },
      {
        path: 'admin/branding',
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./features/admin/branding/admin-branding.component')
            .then(m => m.AdminBrandingComponent)
      },
      {
        path: 'admin/rankings',
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./features/admin/rankings/admin-rankings.component')
            .then(m => m.AdminRankingsComponent)
      },
      {
        path: 'admin/clubs',
        canActivate: [adminGuard],
        loadComponent: () =>
          import('./features/admin/clubs/admin-clubs.component')
            .then(m => m.AdminClubsComponent)
      },
    ]
  },

  { path: '**', redirectTo: '' }
];
